import React, { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";
import "./AcceptInvitePage.css";

/* ============================================================
   AcceptInvitePage — where an invited person lands.
   ----------------------------------------------------------------
   1. An admin invites someone (Users page → invite-user). Supabase emails
      them a link whose redirectTo is this page (INVITE_REDIRECT_TO,
      default https://proposals.estimator.trade/accept-invite).
   2. The link signs them in: `#access_token=...&type=invite` in the hash,
      which supabase-js turns into a session.
   3. They choose a password here (an invited login has none — without this
      step they could never sign in again).
   4. accept-invite adds them to the inviting company, matched by their
      VERIFIED email, and answers with a reason code; each code gets its own
      message below. Then they continue into the app.

   Looks like the desktop login (AuthPage): same gradient, card, inputs and
   button (AcceptInvitePage.css). Public route (index.tsx and App.tsx), like
   /reset-password: the auth gate must not intercept it.
   ============================================================ */

export type AcceptCode =
  | "joined"
  | "already_member_same_company"
  | "member_of_other_company"
  | "email_not_confirmed"
  | "invite_revoked"
  | "invite_already_used"
  | "no_open_invite";

export type AcceptInviteResult = {
  ok?: boolean;
  accepted?: boolean;
  code?: AcceptCode;
  reason?: string;
  error?: string;
};

type Status =
  | { kind: "verifying" }
  | { kind: "idle" }
  | { kind: "busy" }
  | { kind: "joined" }
  | { kind: "error"; message: string }
  | { kind: "expired" }
  | { kind: "refused"; code: AcceptCode };

const READY_TIMEOUT_MS = 6000;
const SUCCESS_REDIRECT_MS = 1400;

/** Joined, or already in that same company → carry on into the app. Reads the
 *  code; falls back to the reason text for an older accept-invite. */
export function inviteLetsUserIn(r: AcceptInviteResult | null | undefined): boolean {
  if (!r || r.error) return false;
  if (r.accepted) return true;
  if (r.code) return r.code === "already_member_same_company";
  return /already a member of this company/i.test(r.reason || "");
}

/** The code, or — for an older accept-invite without codes — a best guess
 *  from its reason text. */
export function resultCode(r: AcceptInviteResult): AcceptCode {
  if (r.code) return r.code;
  const t = (r.reason || "").toLowerCase();
  if (r.accepted) return "joined";
  if (t.includes("already a member of this company")) return "already_member_same_company";
  if (t.includes("another company")) return "member_of_other_company";
  return "no_open_invite";
}

/** What the page says when accept-invite doesn't let the person in. */
export function refusalMessage(code: AcceptCode, email: string | null): string {
  switch (code) {
    case "member_of_other_company":
      return `${email || "This email address"} already belongs to another company on The Estimator. Ask your admin to invite a different address.`;
    case "email_not_confirmed":
      return "We couldn't confirm your email address. Open the invitation link from your email again.";
    case "invite_revoked":
      return "This invitation was cancelled by your company's admin. Ask them to invite you again.";
    case "invite_already_used":
      return "This invitation has already been used. Log in with your email and password instead.";
    case "no_open_invite":
    default:
      return `We couldn't find an open invitation for ${email || "this email address"}. Ask your company's admin to invite this address.`;
  }
}

/* ---------- Preview (test server only) ----------
   /accept-invite?preview=<state> shows a state with sample data and calls
   nothing, so the page can be looked at without a real invite. Ignored on the
   production hosts. */
const PREVIEW_STATES = [
  "verifying", "idle", "busy", "joined", "expired", "error",
  "member_of_other_company", "email_not_confirmed", "invite_revoked",
  "invite_already_used", "no_open_invite",
] as const;
type PreviewState = (typeof PREVIEW_STATES)[number];

function previewState(): PreviewState | null {
  if (typeof window === "undefined") return null;
  const host = window.location.hostname;
  if (host.endsWith("estimator.trade") || host.endsWith("pages.dev")) return null;
  const p = new URLSearchParams(window.location.search).get("preview") as PreviewState | null;
  return p && (PREVIEW_STATES as readonly string[]).includes(p) ? p : null;
}

function statusForPreview(p: PreviewState): Status {
  switch (p) {
    case "verifying": case "idle": case "busy": case "joined": case "expired":
      return { kind: p } as Status;
    case "error":
      return { kind: "error", message: "Passwords don't match." };
    default:
      return { kind: "refused", code: p };
  }
}

export default function AcceptInvitePage() {
  const preview = previewState();
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState<string | null>(preview ? "sales@example.com" : null);
  const [company, setCompany] = useState<string | null>(preview ? "Decks Unique" : null);
  const [status, setStatus] = useState<Status>(
    preview ? statusForPreview(preview) : { kind: "verifying" }
  );

  useEffect(() => {
    if (preview) return;
    let cancelled = false;

    // Supabase puts verify errors (e.g. otp_expired) in the hash.
    const hash = window.location.hash || "";
    if (/error_description=|error=access_denied/i.test(hash)) {
      setStatus({ kind: "expired" });
      return;
    }

    const takeSession = (sess: any) => {
      if (cancelled || !sess) return;
      const u = sess.user || {};
      setEmail(u.email || null);
      // invite-user puts the inviting company's name in the invite metadata.
      const name = String(u.user_metadata?.company_name || "").trim();
      setCompany(name || null);
      setStatus((prev) => (prev.kind === "verifying" ? { kind: "idle" } : prev));
    };

    const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => takeSession(sess));

    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        takeSession(data?.session);
      } catch {
        /* fall through to the timeout */
      }
    })();

    const t = window.setTimeout(() => {
      if (cancelled) return;
      setStatus((prev) => (prev.kind === "verifying" ? { kind: "expired" } : prev));
    }, READY_TIMEOUT_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(t);
      sub.subscription.unsubscribe();
    };
  }, [preview]);

  const submit = async () => {
    if (preview) return;
    if (!pw1 || pw1.length < 8) {
      setStatus({ kind: "error", message: "Password must be at least 8 characters." });
      return;
    }
    if (pw1 !== pw2) {
      setStatus({ kind: "error", message: "Passwords don't match." });
      return;
    }
    setStatus({ kind: "busy" });
    try {
      const { error } = await supabase.auth.updateUser({ password: pw1 });
      if (error) throw error;

      const { data, error: fnErr } = await supabase.functions.invoke("accept-invite", {
        body: {},
      });
      if (fnErr) throw fnErr;
      const result = (data || {}) as AcceptInviteResult;
      if (result.error) throw new Error(result.error);

      if (inviteLetsUserIn(result)) {
        setStatus({ kind: "joined" });
        window.setTimeout(() => window.location.replace("/"), SUCCESS_REDIRECT_MS);
        return;
      }
      // Not let in — say exactly why, and don't leave them signed in to an
      // app that would only say "User not found".
      setStatus({ kind: "refused", code: resultCode(result) });
      try {
        await supabase.auth.signOut();
      } catch {
        /* they can't use the app either way */
      }
    } catch (err: any) {
      const raw = String(err?.message || err || "");
      const message = /auth session missing|jwt|expired|invalid token|not authenticated/i.test(raw)
        ? "This invitation link has expired. Ask your company's admin to send a new invite."
        : /weak password|password.*short|password.*at least|password.*characters/i.test(raw)
        ? "Please choose a stronger password (at least 8 characters)."
        : raw || "Couldn't finish joining. Please try again.";
      setStatus({ kind: "error", message });
    }
  };

  const onEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") submit();
  };

  const goLogin = () => window.location.replace("/");

  const showForm = status.kind === "idle" || status.kind === "busy" || status.kind === "error";
  const busy = status.kind === "busy";
  const title = company ? `Join ${company}` : "Join your company";

  return (
    <div className="ai-page">
      <main className="ai-card" aria-label="Accept invitation">
        <img src={`${process.env.PUBLIC_URL}/icon-192.png`} alt="" className="ai-logo" />
        <h1 className="ai-title">{title}</h1>

        {status.kind === "verifying" && <p className="ai-sub">Checking your invitation…</p>}

        {showForm && (
          <>
            <p className="ai-sub">
              Choose a password{email ? ` for ${email}` : ""}. You'll use it to log in to The
              Estimator.
            </p>
            <div className="ai-form">
              <div className="ai-field">
                <label className="ai-label" htmlFor="ai-pw1">
                  Password
                </label>
                <div className="ai-input-row">
                  <input
                    id="ai-pw1"
                    type={show ? "text" : "password"}
                    className="ai-input"
                    autoComplete="new-password"
                    value={pw1}
                    onChange={(e) => setPw1(e.target.value)}
                    disabled={busy}
                    onKeyDown={onEnter}
                    placeholder="At least 8 characters"
                  />
                  <label className="ai-show">
                    <input
                      type="checkbox"
                      checked={show}
                      onChange={(e) => setShow(e.target.checked)}
                      disabled={busy}
                    />
                    Show
                  </label>
                </div>
              </div>

              <div className="ai-field">
                <label className="ai-label" htmlFor="ai-pw2">
                  Confirm password
                </label>
                <input
                  id="ai-pw2"
                  type={show ? "text" : "password"}
                  className="ai-input"
                  autoComplete="new-password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  disabled={busy}
                  onKeyDown={onEnter}
                />
              </div>

              {status.kind === "error" && (
                <div className="ai-msg ai-msg--err" role="alert">
                  {status.message}
                </div>
              )}

              <button type="button" className="ai-primary" onClick={submit} disabled={busy}>
                {busy ? "Joining…" : "Set password and join"}
              </button>
            </div>
          </>
        )}

        {status.kind === "joined" && (
          <div className="ai-msg" role="status">
            You're in. Taking you to the app…
          </div>
        )}

        {status.kind === "expired" && (
          <div className="ai-msg ai-msg--err" role="alert">
            This invitation link has expired or was already used. Ask your company's admin to send
            a new invite.
          </div>
        )}

        {status.kind === "refused" && (
          <>
            <div className="ai-msg ai-msg--err" role="alert">
              {refusalMessage(status.code, email)}
            </div>
            {status.code === "invite_already_used" && (
              <button type="button" className="ai-primary" onClick={goLogin}>
                Log in
              </button>
            )}
          </>
        )}

        {status.kind !== "joined" && !(status.kind === "refused" && status.code === "invite_already_used") && (
          <div className="ai-footer">
            <button type="button" className="ai-link" onClick={goLogin} disabled={busy}>
              Back to log in
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
